/**
 * Admin-maintained Storage projections on pmProjectFiles.
 *
 * Canonical Portal Access Contract: docs/PORTAL_ACCESS_CONTRACT.md
 * CRM-keyed share rewrites via Align are COMPATIBILITY-ONLY repair; preferred
 * writes are Auth-keyed shares + storageViewAuthUids projection.
 *
 * Canonical View source remains projectFileShares. Cloud Storage may access
 * at most TWO unique Firestore documents per evaluation, so versioned reads
 * cannot look up share docs or users/{uid}.
 *
 * storageViewAuthUids: Auth UIDs from canonical live View shares whose
 * exact-fileId share is visibility=shared and canView is missing/not false.
 * CRM roster leftovers, email-only recipients, C/R-without-View, and Admin
 * UIDs never enter this list. Roster authUid is not required once a share is
 * already Auth-keyed. Arrays are normalized (deduped + sorted) before store
 * or compare; order is never an authorization change.
 *
 * storageAdminAuthUids: Auth UIDs of admins who uploaded, replaced, or ran
 * Align. Separate from View. Versioned Admin read uses only the file document.
 */

import {
  planLinkedAuthAssignmentUpserts,
  planLinkedAuthShareRewrites,
} from "./projectFileShareIds";

function safeStr(value) {
  return String(value ?? "").trim();
}

/** Dedupe, drop blanks, and sort Auth UID lists. Order is not authorization. */
export function normalizeStorageAuthUidList(ids) {
  const seen = new Set();
  const out = [];
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    const v = safeStr(id);
    if (!v || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  });
  return out.sort((a, b) => a.localeCompare(b));
}

function uniqueSorted(ids) {
  return normalizeStorageAuthUidList(ids);
}

function crmOnlyRosterUidsFromCollaborators(collaborators = []) {
  const crm = new Set();
  (Array.isArray(collaborators) ? collaborators : []).forEach((row) => {
    const rosterUid = safeStr(row?.uid || row?.userId);
    const authUid = safeStr(row?.authUid);
    if (rosterUid && rosterUid !== authUid) crm.add(rosterUid);
  });
  return crm;
}

function isLiveProjectFile(file) {
  if (!file || typeof file !== "object") return false;
  const st = safeStr(file.status || "active").toLowerCase();
  if (st === "deleted" || file.deleted === true) return false;
  return Boolean(safeStr(file.id || file.fileId));
}

function isDrawingLibraryFile(file) {
  const lt = safeStr(file?.libraryType).toLowerCase();
  const cat = safeStr(file?.category).toLowerCase();
  const domain = safeStr(file?.projectFileDomain).toLowerCase();
  return lt === "drawing" || cat === "drawings" || domain === "drawing";
}

function storageSchemaVersionOf(file) {
  const n = Number(file?.storageSchemaVersion);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function hasVersionedOriginalPath(file) {
  const p = safeStr(file?.storagePath || file?.path || "");
  return /^pmProjectFiles\/[^/]+\/files\/[^/]+\/original$/.test(p);
}

/** Align and share writers only maintain projections on versioned schema-2 drawings. */
export function isStorageProjectionRepairCandidate(file) {
  if (!isLiveProjectFile(file)) return false;
  if (!isDrawingLibraryFile(file)) return false;
  if (storageSchemaVersionOf(file) < 2) return false;
  if (!hasVersionedOriginalPath(file)) return false;
  return true;
}

export function shareGrantsExactFileView(share, fileId) {
  const fid = safeStr(fileId);
  if (!share || !fid) return false;
  if (safeStr(share.fileId) !== fid) return false;
  const vis = safeStr(share.visibility || "shared").toLowerCase();
  if (vis !== "shared") return false;
  if (share.canView === false) return false;
  if (share.shared === false) return false;
  return true;
}

/** Explicit collaborator.authUid only. Roster/CRM uid is never treated as Auth. */
export function linkedFirebaseAuthUidsFromCollaborators(collaborators = []) {
  return uniqueSorted(
    (Array.isArray(collaborators) ? collaborators : []).map((row) => safeStr(row?.authUid))
  );
}

export function buildStorageViewAuthUids({ fileId, shares = [], collaborators = [] } = {}) {
  const fid = safeStr(fileId);
  const crmOnly = crmOnlyRosterUidsFromCollaborators(collaborators);
  const uids = [];
  (Array.isArray(shares) ? shares : []).forEach((share) => {
    if (!shareGrantsExactFileView(share, fid)) return;
    const uid = safeStr(share.sharedWithUid);
    if (!uid) return;
    if (crmOnly.has(uid)) return;
    uids.push(uid);
  });
  return uniqueSorted(uids);
}

export function storageViewAuthUidsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  const na = uniqueSorted(a);
  const nb = uniqueSorted(b);
  return na.length === nb.length && na.every((v, i) => v === nb[i]);
}

export function isStorageViewAuthProjectionStale(file, desiredUids) {
  if (!file || typeof file !== "object") return true;
  if (!Object.prototype.hasOwnProperty.call(file, "storageViewAuthUids")) return true;
  if (!Array.isArray(file.storageViewAuthUids)) return true;
  return !storageViewAuthUidsEqual(file.storageViewAuthUids, desiredUids);
}

/** Union existing admin Auth UIDs with the authenticated Admin performing the write. Never copies View. */
export function buildStorageAdminAuthUids({ file = null, actorUid = "", existingUids } = {}) {
  const fromFile = Array.isArray(file?.storageAdminAuthUids) ? file.storageAdminAuthUids : [];
  const fromArg = Array.isArray(existingUids) ? existingUids : fromFile;
  return uniqueSorted([...fromArg, safeStr(actorUid)]);
}

export function isStorageAdminAuthProjectionStale(file, desiredUids) {
  if (!file || typeof file !== "object") return true;
  if (!Object.prototype.hasOwnProperty.call(file, "storageAdminAuthUids")) return true;
  if (!Array.isArray(file.storageAdminAuthUids)) return true;
  return !storageViewAuthUidsEqual(file.storageAdminAuthUids, desiredUids);
}

export function applyShareOpsToShareList(shares = [], ops = []) {
  const byId = new Map();
  (Array.isArray(shares) ? shares : []).forEach((share) => {
    const id = safeStr(share?.id || share?.__docId);
    if (!id) return;
    byId.set(id, { ...share, id, __docId: id });
  });
  (Array.isArray(ops) ? ops : []).forEach((op) => {
    const id = safeStr(op?.refId);
    if (!id) return;
    if (op.kind === "delete") {
      byId.delete(id);
      return;
    }
    if (op.kind === "set") {
      const prev = byId.get(id) || {};
      const data = op.data && typeof op.data === "object" ? op.data : {};
      byId.set(id, {
        ...prev,
        ...data,
        id,
        __docId: id,
        fileId: safeStr(data.fileId || op.fileId || prev.fileId),
      });
    }
  });
  return [...byId.values()];
}

/**
 * Admin-only: storageAdminAuthUids projection for versioned drawing reads.
 * Non-admins must not run this (Firestore rules also gate writes).
 */
export function planAdminStorageAdminAuthProjectionUpdates(
  params = {},
  { isGlobalAdmin = false } = {}
) {
  if (!isGlobalAdmin) return [];
  const actor = safeStr(params?.actorUid);
  if (!actor) return [];
  return planStorageViewAuthProjectionUpdates(params).filter((update) =>
    Object.prototype.hasOwnProperty.call(update, "storageAdminAuthUids")
  );
}

export function planStorageViewAuthProjectionUpdates({
  collaborators = [],
  shares = [],
  projectFiles = [],
  shareOps = [],
  actorUid = "",
} = {}) {
  const nextShares = applyShareOpsToShareList(shares, shareOps);
  const actor = safeStr(actorUid);
  const updates = [];
  (Array.isArray(projectFiles) ? projectFiles : []).forEach((file) => {
    if (!isStorageProjectionRepairCandidate(file)) return;
    const fileId = safeStr(file.id || file.fileId);
    if (!fileId) return;
    const desiredView = buildStorageViewAuthUids({
      fileId,
      shares: nextShares,
      collaborators,
    });
    const desiredAdmin = actor ? buildStorageAdminAuthUids({ file, actorUid: actor }) : null;
    const viewStale = isStorageViewAuthProjectionStale(file, desiredView);
    const adminStale = actor ? isStorageAdminAuthProjectionStale(file, desiredAdmin) : false;
    if (!viewStale && !adminStale) return;
    const update = { fileId };
    if (viewStale) update.storageViewAuthUids = desiredView;
    if (actor && adminStale) update.storageAdminAuthUids = desiredAdmin;
    updates.push(update);
  });
  return updates;
}

/**
 * Align: rewrite CRM-keyed View shares onto linked Auth UIDs, then rebuild
 * missing/stale storageViewAuthUids. Projection-only files (already Auth-keyed)
 * still produce updates so the current package can be repaired.
 * Also upserts Auth-keyed projectAssignments so drawing GATE 1 can succeed.
 */
export function planAlignLinkedAccountAccess({
  collaborators = [],
  shares = [],
  projectFiles = [],
  projectId = "",
  actorUid = "",
  existingAssignments = [],
} = {}) {
  const shareOps = planLinkedAuthShareRewrites({
    collaborators,
    shares,
    projectFiles,
    projectId,
  });
  const projectionUpdates = planStorageViewAuthProjectionUpdates({
    collaborators,
    shares,
    projectFiles,
    shareOps,
    actorUid,
  });
  const assignmentOps = planLinkedAuthAssignmentUpserts({
    projectId,
    collaborators,
    existingAssignments,
  });
  return {
    shareOps,
    projectionUpdates,
    assignmentOps,
    setCount: shareOps.filter((op) => op.kind === "set").length,
    deleteCount: shareOps.filter((op) => op.kind === "delete").length,
    projectionCount: projectionUpdates.length,
    assignmentCount: assignmentOps.length,
  };
}

export function groupShareAndProjectionOpsByFile(shareOps = [], projectionUpdates = []) {
  const map = new Map();
  const ensure = (fileId) => {
    const fid = safeStr(fileId);
    if (!fid) return null;
    if (!map.has(fid)) map.set(fid, { fileId: fid, shareOps: [], projectionUpdates: [] });
    return map.get(fid);
  };
  (Array.isArray(shareOps) ? shareOps : []).forEach((op) => {
    const group = ensure(op.fileId);
    if (group) group.shareOps.push(op);
  });
  (Array.isArray(projectionUpdates) ? projectionUpdates : []).forEach((update) => {
    const group = ensure(update.fileId);
    if (group) group.projectionUpdates.push(update);
  });
  return [...map.values()];
}

export function appendShareAndProjectionOpsToTransaction(
  tx,
  { db, doc, serverTimestamp, shareOps = [], projectionUpdates = [] } = {}
) {
  (Array.isArray(shareOps) ? shareOps : []).forEach((op) => {
    const refId = safeStr(op.refId);
    if (!refId) return;
    const refDoc = doc(db, "projectFileShares", refId);
    if (op.kind === "set") {
      tx.set(
        refDoc,
        {
          ...(op.data || {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else if (op.kind === "delete") {
      tx.delete(refDoc);
    }
  });
  (Array.isArray(projectionUpdates) ? projectionUpdates : []).forEach((update) => {
    const fileId = safeStr(update.fileId);
    if (!fileId) return;
    const patch = { updatedAt: serverTimestamp() };
    if (Object.prototype.hasOwnProperty.call(update, "storageViewAuthUids")) {
      patch.storageViewAuthUids = normalizeStorageAuthUidList(update.storageViewAuthUids);
    }
    if (Object.prototype.hasOwnProperty.call(update, "storageAdminAuthUids")) {
      patch.storageAdminAuthUids = normalizeStorageAuthUidList(update.storageAdminAuthUids);
    }
    tx.update(doc(db, "pmProjectFiles", fileId), patch);
  });
}

export function appendShareAndProjectionOpsToBatch(batch, { db, doc, serverTimestamp, shareOps = [], projectionUpdates = [] } = {}) {
  (Array.isArray(shareOps) ? shareOps : []).forEach((op) => {
    const refId = safeStr(op.refId);
    if (!refId) return;
    const refDoc = doc(db, "projectFileShares", refId);
    if (op.kind === "set") {
      batch.set(
        refDoc,
        {
          ...(op.data || {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else if (op.kind === "delete") {
      batch.delete(refDoc);
    }
  });
  (Array.isArray(projectionUpdates) ? projectionUpdates : []).forEach((update) => {
    const fileId = safeStr(update.fileId);
    if (!fileId) return;
    const patch = { updatedAt: serverTimestamp() };
    if (Object.prototype.hasOwnProperty.call(update, "storageViewAuthUids")) {
      patch.storageViewAuthUids = normalizeStorageAuthUidList(update.storageViewAuthUids);
    }
    if (Object.prototype.hasOwnProperty.call(update, "storageAdminAuthUids")) {
      patch.storageAdminAuthUids = normalizeStorageAuthUidList(update.storageAdminAuthUids);
    }
    batch.update(doc(db, "pmProjectFiles", fileId), patch);
  });
}

/** One batch when small; otherwise one batch per file so share + projection stay atomic. */
/** Global admin: add actor to storageAdminAuthUids on versioned drawings (Storage read gate). */
export async function commitAdminStorageAdminAuthProjectionIfNeeded({
  db,
  writeBatch,
  doc,
  serverTimestamp,
  commitShareOpsWithProjection: commitFn = commitShareOpsWithProjection,
  isGlobalAdmin = false,
  actorUid = "",
  collaborators = [],
  shares = [],
  projectFiles = [],
} = {}) {
  const updates = planAdminStorageAdminAuthProjectionUpdates(
    {
      collaborators,
      shares,
      projectFiles,
      shareOps: [],
      actorUid,
    },
    { isGlobalAdmin }
  );
  if (!updates.length) return { committed: false, updateCount: 0 };
  try {
    await commitFn({
      db,
      writeBatch,
      doc,
      serverTimestamp,
      shareOps: [],
      projectionUpdates: updates,
    });
    return { committed: true, updateCount: updates.length };
  } catch (error) {
    return {
      committed: false,
      updateCount: 0,
      error: {
        code: safeStr(error?.code) || "projection_commit_failed",
        message: safeStr(error?.message) || String(error),
      },
    };
  }
}

export async function commitShareOpsWithProjection({
  db,
  writeBatch,
  doc,
  serverTimestamp,
  shareOps = [],
  projectionUpdates = [],
} = {}) {
  const shares = Array.isArray(shareOps) ? shareOps : [];
  const projections = Array.isArray(projectionUpdates) ? projectionUpdates : [];
  if (!shares.length && !projections.length) return { batches: 0 };

  const total = shares.length + projections.length;
  if (total <= 400) {
    const batch = writeBatch(db);
    appendShareAndProjectionOpsToBatch(batch, { db, doc, serverTimestamp, shareOps: shares, projectionUpdates: projections });
    await batch.commit();
    return { batches: 1 };
  }

  const groups = groupShareAndProjectionOpsByFile(shares, projections);
  for (const group of groups) {
    const batch = writeBatch(db);
    appendShareAndProjectionOpsToBatch(batch, {
      db,
      doc,
      serverTimestamp,
      shareOps: group.shareOps,
      projectionUpdates: group.projectionUpdates,
    });
    await batch.commit();
  }
  return { batches: groups.length };
}
